import { useState } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { apiV2, type V2StatusPage, type EnrichedMonitor } from '@/lib/api-v2-client'
import { useAuth } from '@/hooks/use-auth'
import { isFreeTier } from '@/lib/subscription-access'
import { toast } from 'sonner'
import { listAllMonitors } from '@/lib/list-all-monitors'

export const Route = createFileRoute('/_authenticated/dashboard/status-pages/$id/edit')({
  component: EditStatusPage,
})

function EditStatusPage() {
  const { id } = Route.useParams()

  const { data: pageResponse, isLoading } = useQuery({
    queryKey: ['status-page', id],
    queryFn: () => apiV2.getStatusPage(id),
  })

  const { data: monitorsResponse } = useQuery({
    queryKey: ['monitors', 'all'],
    queryFn: () => listAllMonitors(),
  })

  const detail = pageResponse?.data
  const page = detail?.page
  const allMonitors = monitorsResponse?.data ?? []

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 animate-pulse rounded bg-muted" />
        <div className="h-96 animate-pulse rounded-lg bg-muted" />
      </div>
    )
  }

  if (!page) {
    return (
      <div className="py-12 text-center">
        <h1 className="text-xl font-semibold">Status page not found</h1>
        <Button asChild className="mt-4">
          <Link to="/dashboard/status-pages">Back to Status Pages</Link>
        </Button>
      </div>
    )
  }

  return (
    <StatusPageForm
      key={page.id}
      page={page}
      initialMonitorIds={detail.monitors.map((m) => m.monitor_id)}
      allMonitors={allMonitors}
    />
  )
}

function StatusPageForm({
  page,
  initialMonitorIds,
  allMonitors,
}: {
  page: V2StatusPage
  initialMonitorIds: string[]
  allMonitors: EnrichedMonitor[]
}) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  // The free plan's status page has no email subscribers and keeps the "Powered by Uptrack"
  // link (the server answers 402 when a request turns either on). Turning one off is allowed.
  const { organization } = useAuth()
  const freeTier = isFreeTier(organization)

  const [name, setName] = useState(page.name)
  const [slug, setSlug] = useState(page.slug)
  const [description, setDescription] = useState(page.description ?? '')
  const [isPublic, setIsPublic] = useState(page.is_public)
  const [allowSubscriptions, setAllowSubscriptions] = useState(page.allow_subscriptions)
  const [whitelabel, setWhitelabel] = useState(page.whitelabel ?? false)
  const [noindex, setNoindex] = useState(page.noindex ?? false)
  const [themeConfig, setThemeConfig] = useState<Record<string, string>>(
    (page.theme_config ?? {}) as Record<string, string>,
  )
  const [selectedMonitorIds, setSelectedMonitorIds] = useState<string[]>(
    initialMonitorIds ?? []
  )

  const updateMutation = useMutation({
    mutationFn: () =>
      apiV2.updateStatusPage(page.id, {
        name,
        slug,
        // An empty string clears it: a missing field keeps the old description on the server.
        description,
        is_public: isPublic,
        allow_subscriptions: allowSubscriptions,
        whitelabel,
        noindex,
        theme_config: themeConfig,
        monitor_ids: selectedMonitorIds,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['status-pages'] })
      queryClient.invalidateQueries({ queryKey: ['status-page', page.id] })
      toast.success('Status page updated')
      navigate({ to: '/dashboard/status-pages' })
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to update status page')
    },
  })

  function toggleMonitor(monitorId: string) {
    setSelectedMonitorIds((prev) =>
      prev.includes(monitorId)
        ? prev.filter((mid) => mid !== monitorId)
        : [...prev, monitorId]
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-semibold">Edit Status Page</h1>
          <p className="text-muted-foreground">Update {page.name}</p>
        </div>
        <Button variant="outline" asChild className="w-full sm:w-auto">
          <Link to="/dashboard/status-pages">Cancel</Link>
        </Button>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault()
          updateMutation.mutate()
        }}
        className="space-y-6"
      >
        <Card>
          <CardHeader>
            <CardTitle>Details</CardTitle>
            <CardDescription>Basic information about your status page</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="slug">Slug</Label>
              <Input
                id="slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                required
                pattern="[a-z0-9][a-z0-9-]*[a-z0-9]"
                minLength={3}
              />
              <p className="text-xs text-muted-foreground">
                Available at /status/{slug || 'your-slug'}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Current status of our services"
              />
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                  className="h-4 w-4 rounded border-input accent-primary"
                />
                Public
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={allowSubscriptions}
                  onChange={(e) => setAllowSubscriptions(e.target.checked)}
                  disabled={freeTier && !page.allow_subscriptions}
                  className="h-4 w-4 rounded border-input accent-primary"
                />
                Allow email subscriptions
                {freeTier && <span className="text-sm text-muted-foreground">(Pro)</span>}
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={noindex}
                  onChange={(e) => setNoindex(e.target.checked)}
                  className="h-4 w-4 rounded border-input accent-primary"
                />
                Hide from search engines
                <span className="text-xs text-muted-foreground">(Team+)</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={whitelabel}
                  onChange={(e) => setWhitelabel(e.target.checked)}
                  disabled={freeTier && !(page.whitelabel ?? false)}
                  className="h-4 w-4 rounded border-input accent-primary"
                />
                Remove "Powered by Uptrack" branding
                <span className="text-xs text-muted-foreground">(Business)</span>
              </label>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Theme <span className="text-xs font-normal text-muted-foreground">(Pro+)</span></CardTitle>
            <CardDescription>Customize the look of your public status page</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Primary Color</Label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    aria-label="Primary color picker"
                    value={themeConfig.primary_color || '#1E3A5F'}
                    onChange={(e) => setThemeConfig({ ...themeConfig, primary_color: e.target.value })}
                    className="h-9 w-12 cursor-pointer rounded border p-1"
                  />
                  <Input
                    aria-label="Primary color value"
                    value={themeConfig.primary_color || ''}
                    onChange={(e) => setThemeConfig({ ...themeConfig, primary_color: e.target.value })}
                    placeholder="#1E3A5F"
                    className="flex-1"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Background Color</Label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    aria-label="Background color picker"
                    value={themeConfig.background_color || '#ffffff'}
                    onChange={(e) => setThemeConfig({ ...themeConfig, background_color: e.target.value })}
                    className="h-9 w-12 cursor-pointer rounded border p-1"
                  />
                  <Input
                    aria-label="Background color value"
                    value={themeConfig.background_color || ''}
                    onChange={(e) => setThemeConfig({ ...themeConfig, background_color: e.target.value })}
                    placeholder="#ffffff"
                    className="flex-1"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Text Color</Label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    aria-label="Text color picker"
                    value={themeConfig.text_color || '#333333'}
                    onChange={(e) => setThemeConfig({ ...themeConfig, text_color: e.target.value })}
                    className="h-9 w-12 cursor-pointer rounded border p-1"
                  />
                  <Input
                    aria-label="Text color value"
                    value={themeConfig.text_color || ''}
                    onChange={(e) => setThemeConfig({ ...themeConfig, text_color: e.target.value })}
                    placeholder="#333333"
                    className="flex-1"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="sp-font-family">Font Family</Label>
                <Input
                  id="sp-font-family"
                  value={themeConfig.font_family || ''}
                  onChange={(e) => setThemeConfig({ ...themeConfig, font_family: e.target.value })}
                  placeholder="Inter, system-ui, sans-serif"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Monitors</CardTitle>
            <CardDescription>
              Select which monitors appear on this status page ({selectedMonitorIds.length} selected)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {allMonitors.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No monitors found. Create monitors first to add them to your status page.
              </p>
            ) : (
              <div className="space-y-2">
                {allMonitors.map((monitor) => (
                  <label
                    key={monitor.id}
                    className="flex items-center gap-3 rounded-lg border p-3 hover:bg-muted/50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedMonitorIds.includes(monitor.id)}
                      onChange={() => toggleMonitor(monitor.id)}
                      className="h-4 w-4 rounded border-input accent-primary"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium">{monitor.name}</p>
                      <p className="text-xs text-muted-foreground">{monitor.url}</p>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button variant="outline" asChild>
            <Link to="/dashboard/status-pages">Cancel</Link>
          </Button>
          <Button type="submit" disabled={updateMutation.isPending}>
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </div>
  )
}
